import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import novaOpsApi from '../../../lib/novaOpsApi';
import {
  hasWalletSelector,
  walletSelectorQuery,
  type WalletScope,
  type WalletSummaryResponse,
  type WalletWithdrawalRiskResponse,
  type WalletWithdrawalsResponse,
} from './walletTypes';

const scopeKey = (scope: WalletScope) => [
  scope.mvno ?? null,
  scope.appUserUuid ?? null,
  scope.iCustomer ?? null,
];

export const walletQueryKeys = {
  summary: (scope: WalletScope) => ['wallet', 'summary', ...scopeKey(scope)],
  withdrawals: (scope: WalletScope) => ['wallet', 'withdrawals', ...scopeKey(scope)],
  risk: (scope: WalletScope) => ['wallet', 'risk', scope.mvno ?? null, scope.appUserUuid ?? null],
};

export function useWalletSummary(scope: WalletScope) {
  return useQuery({
    queryKey: walletQueryKeys.summary(scope),
    enabled: hasWalletSelector(scope),
    queryFn: async () => {
      const { data } = await novaOpsApi.get<WalletSummaryResponse>(
        `/ops/customers/wallet-summary${walletSelectorQuery(scope)}`,
      );
      return data ?? null;
    },
  });
}

export function useWalletWithdrawals(scope: WalletScope) {
  return useQuery({
    queryKey: walletQueryKeys.withdrawals(scope),
    enabled: hasWalletSelector(scope),
    queryFn: async () => {
      const { data } = await novaOpsApi.get<WalletWithdrawalsResponse>(
        `/ops/customers/wallet-withdrawals${walletSelectorQuery(scope)}`,
      );
      return data ?? null;
    },
  });
}

/** Withdrawal-risk holds exist per app user only; there is nothing to ask for an account-only scope. */
export function useWalletWithdrawalRisk(scope: WalletScope) {
  return useQuery({
    queryKey: walletQueryKeys.risk(scope),
    enabled: Boolean(scope.appUserUuid),
    queryFn: async () => {
      const params = new URLSearchParams({ app_user_uuid: scope.appUserUuid! });
      if (scope.mvno) params.set('mvno', scope.mvno);
      const { data } = await novaOpsApi.get<WalletWithdrawalRiskResponse>(
        `/ops/customers/wallet-withdrawal-risk?${params.toString()}`,
      );
      return data ?? null;
    },
  });
}

export function useReleaseWalletHold(scope: WalletScope) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (reason: string) => {
      await novaOpsApi.post('/ops/customers/wallet-withdrawal-risk/release', {
        app_user_uuid: scope.appUserUuid,
        mvno: scope.mvno ?? undefined,
        reason,
      });
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: walletQueryKeys.risk(scope) }),
  });
}

export function useRefreshWallet(scope: WalletScope) {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: walletQueryKeys.summary(scope) }),
      queryClient.invalidateQueries({ queryKey: walletQueryKeys.withdrawals(scope) }),
      queryClient.invalidateQueries({ queryKey: walletQueryKeys.risk(scope) }),
    ]);
}
